import { FC, useRef } from 'react';
import classNames from 'classnames';
import { SwimlaneStyle, SwimlaneType } from 'ContentLayout';

import { SwimlaneItem } from '@rikstv/play-common/src/forces/assetDetails/assetDetails.types';
import { PadlockIcon } from '@rikstv/play-common/src/icons';
import { CssBemHelper } from '@rikstv/play-common/src/utils/css/CssBemHelper';
import { isOneOf } from '@rikstv/play-common/src/utils/types/typeUtils';
import { H3, Meta, SubBody, Tag, TertiaryIconButton } from '@rikstv/shared-components';

import { getImagePackSet, ImagePackSetVariation } from '../../../utils/imagePack/imagePack.utils';
import { AssetImage, AssetImageProps } from '../../assetImage/AssetImage';
import { AssetImageContainer } from '../../assetImage/AssetImageContainer';
import { Breakpoints } from '../../grid/breakpoints';
import { SwimlaneItemImageOverlay } from '../../imageOverlays';
import { ImdbBadge } from '../../imdbBadge/ImdbBadge';
import { Pill } from '../../pill/Pill';
import { usePinStore } from '../../pin/usePinStore';
import { ProviderImageWrapper } from '../../providerImage/ProviderImageWrapper';
import { ClampLines } from '../../truncateText/ClampLines';
import { SwimlaneItemProps } from '../Swimlane.types';
import { getLinkTo } from '../swimlane.utils';

import { ItemWrapper } from './ItemWrapper';
import { menuIconsImageConfig, menuThumbnailItemImageConfig } from './MenuSwimlaneItem';
import { heroPortraitImageConfig, standardPortraitImageConfig } from './PortraitItem';

import './StandardItem.scss';

interface OwnProps {
  showImdbRating?: ImdbProps['showImdbRating'];
  featured?: boolean;
  onClick?: () => void;
  preventNavigation?: boolean;
  noMargin?: boolean;
  showDescription?: boolean;
  swimlaneType: SwimlaneType;
  swimlaneStyle: SwimlaneStyle;
  classes?: string;
  showProviderLogos?: boolean;
  contextMenuItems?: ContextMenuItem[];
  hideCaption?: boolean;
  centeredTitle?: boolean;
}

export interface ContextMenuItem {
  icon: () => JSX.Element;
  onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, item: SwimlaneItem) => void;
  title: string;
  ariaLabel: string;
}

export type StandardItemProps = SwimlaneItemProps & OwnProps;

const bem = new CssBemHelper('swimlane-standard-item');

export const StandardItem: FC<StandardItemProps> = ({
  asset,
  swimlaneType,
  swimlaneStyle,
  featured,
  showImdbRating = asset.programState.type === 'Live' ? 'never' : 'onHover',
  noMargin = false,
  preventNavigation,
  showDescription = false,
  onClick,
  classes = '',
  showProviderLogos = true,
  contextMenuItems,
  hideCaption = false,
  centeredTitle = false,
}) => {
  const renderImageOverlay = swimlaneType !== 'MyList';
  const titleRef = useRef<HTMLHeadingElement>(null);
  const metaRef = useRef<HTMLElement>(null);
  const titleBem = new CssBemHelper(bem.element('title'));

  addScrollIfTextOverflow([titleRef, metaRef]);

  return (
    <ItemWrapper
      className={classNames(bem.base, classes, {
        [bem.modifier('featured')]: featured,
        [bem.modifier('no-margin')]: noMargin,
      })}
      linkTo={getLinkTo(asset)}
      preventDefault={preventNavigation}
      onClick={onClick}>
      <AssetImageContainer circle={swimlaneStyle === 'MenuCircle'}>
        <StandardImage image={asset.image.url} blackWhite={asset.image.bwFilter} style={swimlaneStyle} />
        {renderImageOverlay && <SwimlaneItemImageOverlay programState={asset.programState} />}
        <AgeLimitPill ageLimit={asset.ageLimit}>
          {/* ImdbPill will be rendered if ageLimit isn't relevant */}
          <ImdbPill
            imdbRating={asset.imdbRating}
            showImdbRating={showImdbRating}
            alignRight={asset.programState.type === 'Live'}
          />
        </AgeLimitPill>
        {showProviderLogos && (
          <ProviderImageWrapper
            logos={asset.logos}
            portrait={isOneOf(swimlaneStyle, [
              'StandardPortrait',
              'NumberedPortrait',
              'HeroPortrait',
              'SwimmingPoolPortrait',
            ])}
          />
        )}
        <RightBottomLabel label={asset.rightBottomLabel} />
      </AssetImageContainer>
      <div className={hideCaption ? 'sr-only' : ''} style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ overflow: 'hidden', flexGrow: 1 }}>
          <H3
            ref={titleRef}
            lookLike="title-4"
            className={classNames(titleBem.base, {
              [titleBem.modifier('centered')]: centeredTitle,
            })}
            title={titleRef.current?.classList.contains('scrollable') ? asset.title : undefined}>
            {asset.title}
          </H3>
          <Meta ref={metaRef} className={bem.element('meta')}>
            {asset.subtitle}
          </Meta>
        </div>
        {contextMenuItems &&
          contextMenuItems.map((contextMenuItem, idx) => {
            return (
              <TertiaryIconButton
                key={`contextMenuItem-${idx}-${asset.cardId}`}
                title={contextMenuItem.title}
                aria-label={contextMenuItem.ariaLabel}
                className="swimlane-standard-item__context-menu"
                icon={contextMenuItem.icon}
                onClick={e => {
                  contextMenuItem.onClick(e, asset);
                }}
              />
            );
          })}
      </div>
      {showDescription && (
        <ClampLines textStyle={SubBody} lines={3}>
          {asset.description}
        </ClampLines>
      )}
    </ItemWrapper>
  );
};

const StandardImage: FC<{ image: string; blackWhite: boolean; style: SwimlaneStyle }> = ({
  image,
  blackWhite,
  style,
}) => {
  const imageVariations = getImagePackSet({
    url: image,
    variations: getImageConfigFromStyle(style),
  });

  return <AssetImage imageSet={imageVariations} ratio={getRatioFromStyle(style)} alt="" blackWhite={blackWhite} />;
};

interface ImdbProps {
  imdbRating?: string;
  showImdbRating: 'onHover' | 'always' | 'never';
  alignRight?: boolean;
}
const pillBem = new CssBemHelper(bem.element('imdb-pill'));
const ImdbPill: FC<ImdbProps> = ({ imdbRating, showImdbRating, alignRight = false }) => {
  if (!imdbRating || showImdbRating === 'never') {
    return null;
  }
  return (
    <Pill
      className={classNames(pillBem.base, {
        [pillBem.modifier('show-on-hover')]: showImdbRating === 'onHover',
        [pillBem.modifier('right')]: alignRight,
      })}>
      <ImdbBadge imdbRating={imdbRating} />
    </Pill>
  );
};

export const AgeLimitPill = ({ ageLimit, children }: { ageLimit?: number; children?: React.ReactNode }) => {
  const { assetIsAgeRestricted } = usePinStore();
  const showAgeRating = assetIsAgeRestricted(ageLimit);

  // Used to display IMDb rating for standard items when age limit does not apply
  if (!showAgeRating) {
    return children;
  }

  return (
    <Pill style={{ display: 'flex', alignItems: 'center' }} className={pillBem.base}>
      <PadlockIcon style={{ marginRight: '0.25rem' }} iconSize={12} />
      <Tag>{ageLimit}+</Tag>
    </Pill>
  );
};

/** Displays price tag or TVOD icon for rental assets. */
export const RightBottomLabel = ({ label }: { label?: string }) => {
  if (!label) {
    return;
  }

  return (
    <Tag uppercase={false} className={'swimlane-standard-item_price-pill'}>
      {label}
    </Tag>
  );
};

function getRatioFromStyle(style: SwimlaneStyle): AssetImageProps['ratio'] {
  switch (style) {
    case 'NumberedPortrait':
    case 'StandardPortrait':
    case 'SwimmingPoolPortrait':
    case 'HeroPortrait':
      return 'portrait';
    case 'MenuCircle':
      return 'circle';
    case 'MenuLogo':
      return 'square';
    default:
      return '16_9';
  }
}

function addScrollIfTextOverflow(refArray: React.RefObject<HTMLElement>[]) {
  refArray?.forEach(ref => {
    if (ref?.current) {
      const titleElement = ref.current;

      if (titleElement.scrollWidth > titleElement.offsetWidth) {
        titleElement.classList.add('scrollable');
      }
    }
  });
}

function getImageConfigFromStyle(style: SwimlaneStyle): ImagePackSetVariation[] {
  switch (style) {
    case 'StandardPortrait':
    case 'NumberedPortrait':
    case 'SwimmingPoolPortrait':
      return standardPortraitImageConfig;
    case 'HeroPortrait':
      return heroPortraitImageConfig;
    case 'MenuCircle':
    case 'MenuLogo':
      return menuIconsImageConfig;
    case 'MenuThumbnail':
      return menuThumbnailItemImageConfig;
    default:
      return [
        { width: 230, height: 129, breakpoint: Breakpoints.SM },
        { width: 296, height: 166, breakpoint: Breakpoints.MD },
        { width: 460, height: 258, breakpoint: Breakpoints.DEFAULT },
      ];
  }
}
